/* eslint-disable mui-unused-classes/unused-classes */
import React, { useMemo, useRef, useState } from 'react';
import {
  makeStyles,
  Box,
  InputLabel,
  Typography,
  withStyles,
  // InputAdornment,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import {
  LOCATION_TYPE,
  RENTAL_WIDGET_FIELDS,
  ERRORS_PREFIX,
} from 'common/components/MainWidgetDialog/utils/mainWidgetConstants';
import { MainWidgetTextField } from 'common/components/MainWidgetDialog/MainWidgetTextField';
import IconedBox from 'common/components/MainWidgetDialog/IconedBox';
import clsx from 'clsx';
import { withTranslation } from 'next-i18next';

const useStyles = makeStyles((theme) => ({
  textFieldLabel: ({ bordered }) => ({
    border: bordered ? `1px solid ${theme.palette.grey[100]}` : null,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      border: `1px solid ${theme.palette.grey[100]}`,
    },
  }),
  '& .widgetInputLabel': {
    position: 'absolute',
    left: theme.spacing(6.6),
    fontSize: theme.typography.pxToRem(12),
    top: theme.spacing(1.25),
    color: theme.palette.grey[200],
    zIndex: 1,
  },
  '& .widgetInputIcon': {
    color: theme.palette.grey[600],
    position: 'absolute',
    zIndex: 1,
    top: '50%',
    transform: 'translateY(-50%)',
    left: theme.spacing(2),
  },
}));

const StyledIconedBox = withStyles((theme) => ({
  IconedBoxContainer: ({ error }) => ({
    '& .WidgetInputLabel': {
      position: 'absolute',
      left: theme.spacing(6.6),
      fontSize: theme.typography.pxToRem(13),
      color: error ? theme.palette.error.main : theme.palette.grey[200],
      top: theme.typography.pxToRem(6),
      zIndex: 1,
    },
  }),
}))(IconedBox);

const WidgetLocationField = (props) => {
  const {
    selectedLocationId,
    pickup,
    locationsList = [],
    label,
    placeholder,
    handleInputChange,
    handleLocationChange,
    handleRenderAllWidget,
    loading,
    errors,
    disabled,
    shiftAmount,
    vertical,
    bordered,
    classes: styles = {},
    t,
  } = props;
  const classes = useStyles(props);

  const getFieldType = () => (pickup === LOCATION_TYPE.PICKUP
    ? RENTAL_WIDGET_FIELDS.PICKUP_LOCATION_ID
    : RENTAL_WIDGET_FIELDS.DROPOFF_LOCATION_ID);
  const [isFocused, setIsFocused] = useState('');
  const autoCompleteInputRef = useRef();
  const selectedLocation = useMemo(
    () => locationsList.find((location) => location.id && location.id === selectedLocationId) || null,
    [selectedLocationId, locationsList],
  );

  const placeHolder = useMemo(() => {
    if (isFocused && selectedLocation) {
      return `${selectedLocation.display_name}, ${selectedLocation.region_name}`;
    }
    return placeholder;
  }, [selectedLocation, isFocused, placeholder]);

  const fieldValue = useMemo(() => {
    if (isFocused) return null;
    return selectedLocation;
  }, [isFocused, selectedLocation]);

  return (
    <Box
      bordered={vertical || bordered}
      className={clsx(classes.textFieldLabel, styles.textFieldLabel)}
    >
      <StyledIconedBox Icon={LocationOnIcon} error={!!errors[`${ERRORS_PREFIX}${getFieldType()}`]}>
        <Autocomplete
          value={fieldValue}
          options={loading ? [] : locationsList}
          filterOptions={(x) => x}
          getOptionLabel={(option) => `${option.display_name}, ${option.region_name}`}
          id={`${ERRORS_PREFIX}${getFieldType()}`}
          error={!!errors[`${ERRORS_PREFIX}${getFieldType()}`]}
          popupIcon={null}
          className="WidgetInput"
          autoComplete
          disableClearable
          includeInputInLis
          componentsProps={{
            paper: { shiftAmount },
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onInputChange={(e, value, reason) => {
            if (reason !== 'reset') handleInputChange(pickup, value);
          }}
          disabled={disabled}
          onChange={(e, value) => {
            setIsFocused(true);
            handleLocationChange(getFieldType(), value?.id);
            autoCompleteInputRef.current.blur();
          }}
          onOpen={() => handleRenderAllWidget?.(true)}
          loading={loading}
          renderOption={(option, params) => (
            <li {...params}>
              <Box>
                <Typography
                  dangerouslySetInnerHTML={{
                    __html: `${option.display_name}, <b>${option.region_name}</b>`,
                  }}
                />
                {option.distance >= 0 && (
                  <Typography>
                    {`${option.distance} ${t('fe_es_common_main_widget:miles_away')} ${
                      option.formattedAddress
                    }`}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <>
              <InputLabel className="WidgetInputLabel">{label}</InputLabel>
              <MainWidgetTextField
                {...params}
                inputRef={autoCompleteInputRef}
                placeholder={placeHolder}
                isFocused={isFocused}
                variant="filled"
                error={!!errors[`${ERRORS_PREFIX}${getFieldType()}`]}
              />
            </>
          )}
        />
      </StyledIconedBox>
    </Box>
  );
};
export default withTranslation(['fe_es_common_main_widget'])(WidgetLocationField);
